import { msalInstance, loginRequest } from './msalConfig';
import { authService, UserInfo } from './authService';

export class MicrosoftAuthService {
  /**
   * Sign in with Microsoft using a popup (separate window; main page stays open).
   * Requires Cross-Origin-Opener-Policy: unsafe-none (see vite.config.ts and nginx.conf).
   */
  async signInWithMicrosoft(): Promise<UserInfo> {
    const isTestMode = import.meta.env.VITE_TEST_MODE === 'true';

    if (isTestMode) {
      const response = {
        accessToken: 'test-microsoft-token',
        account: { username: 'test@example.com', name: 'Test User' }
      };
      return authService.loginWithMicrosoft({ access_token: response.accessToken });
    }

    try {
      if (msalInstance && typeof msalInstance.initialize === 'function') {
        try {
          await msalInstance.initialize();
        } catch (initError) {
          console.warn('MSAL initialization (may already be initialized):', initError);
        }
      }
      const response = await msalInstance.loginPopup(loginRequest);
      if (!response?.accessToken) {
        throw new Error('No access token received from Microsoft');
      }
      return authService.loginWithMicrosoft({ access_token: response.accessToken });
    } catch (error: any) {
      console.error('Microsoft authentication error:', error);
      if (error.errorCode === 'user_cancelled') {
        throw new Error('Sign-in was cancelled. Please try again.');
      }
      if (error.errorCode === 'popup_window_error') {
        throw new Error('Popup was blocked. Please allow popups for this site or try again.');
      }
      if (error.errorCode === 'network_error') {
        throw new Error('Network error. Please check your internet connection.');
      }
      if (error.errorCode === 'throttled') {
        throw new Error('Too many failed attempts. Please try again later.');
      }
      throw new Error(`Microsoft authentication failed: ${error.errorMessage || error.message}`);
    }
  }

  /**
   * Call once on app load to handle any in-progress redirect (e.g. from an old session).
   * Returns UserInfo if we just came back from a Microsoft redirect; otherwise null.
   * When using popup for login, this usually returns null.
   */
  async handleRedirectCallback(): Promise<UserInfo | null> {
    const isTestMode = import.meta.env.VITE_TEST_MODE === 'true';
    if (isTestMode) return null;

    try {
      if (!msalInstance || typeof msalInstance.handleRedirectPromise !== 'function') return null;
      const response = await msalInstance.handleRedirectPromise();
      if (!response?.accessToken) return null;
      return authService.loginWithMicrosoft({ access_token: response.accessToken });
    } catch (error) {
      console.error('Microsoft redirect handling error:', error);
      return null;
    }
  }

  /**
   * Sign out from Microsoft (popup).
   */
  async signOut(): Promise<void> {
    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0 && typeof msalInstance.logoutPopup === 'function') {
        await msalInstance.logoutPopup({ account: accounts[0] });
      }
    } catch (error: any) {
      console.error('Microsoft sign out error:', error);
      throw new Error(`Sign out failed: ${error.message}`);
    }
  }

  /**
   * Get current Microsoft user
   */
  getCurrentUser() {
    return msalInstance.getActiveAccount();
  }

  /**
   * Check if user is signed in with Microsoft
   */
  isSignedIn(): boolean {
    const accounts = msalInstance.getAllAccounts();
    return accounts.length > 0;
  }
}

// Create and export a single instance
export const microsoftAuthService = new MicrosoftAuthService();
